import { debug } from '../../../stores/store';
import { AccountRequestSchema, type AccountRequestType } from '../Settings/SettingsApi';
import { defaultAccount } from './defaultAccount';
import { get } from 'svelte/store';
import { LangSettingsSchema, type LangSettingsType } from '../../../locale/LangSettingsType';

export type AccountType = 'standard' | 'trial';

export class Account {
    private readonly _innerId: string;
    private _id: number | null = null;
    private _accountData: AccountRequestType | null = null;

    constructor() {
        this._innerId = Math.random().toString(36);
    }

    public get innerId(): string {
        return this._innerId;
    }

    public get() {
        return this._accountData;
    }

    public set(data: AccountRequestType, id: number | null): this {
        this._id = id;
        this._accountData = JSON.parse(JSON.stringify(data));
        return this;
    }

    public getId() {
        return this._id;
    }

    public isNew() {
        return this._id === null;
    }

    public getName() {
        return this._accountData?.name || '';
    }

    public isTechnical() {
        let cId = this.getCustomerId();
        return cId !== null && cId < 0;
    }

    public setName(name: string) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            name,
        };
        return this;
    }

    public getEmail() {
        return this._accountData?.email || '';
    }

    public setEmail(email: string) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            email,
        };
        return this;
    }

    public getExpirationDate(): string | null {
        return this._accountData?.expiration_date || null;
    }

    public setExpirationDate(expiration_date: string | null) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            expiration_date,
        };
        return this;
    }

    public getCustomerId(): number | null {
        return this._accountData?.customer_id || null;
    }

    public setCustomerId(customer_id: number | null) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            customer_id,
        };
        return this;
    }

    public getMasterCustomerId(): number | null {
        return this._accountData?.master_customer_id ?? null;
    }

    public getType(): AccountType {
        if (this.getExpirationDate() !== null) {
            return 'standard';
        }
        return 'trial';
    }

    public getWebuserId(): number | null {
        return this._accountData?.webuser_id || null;
    }

    public isExpired(): boolean {
        let getExpirationDate = this.getExpirationDate();
        if (getExpirationDate === null) {
            return false;
        }
        // if (this._accountData?.is_expired !== null) {
        return this._accountData?.is_expired || false;
        // } else {
        //     const expirationDate = new Date(getExpirationDate);
        //     expirationDate.setHours(23, 59, 59, 999);
        //     const today = new Date();
        //     return expirationDate < today;
        // }
    }

    public getEnabledMapLayers() {
        return this._accountData?.settings_readonly.enabled_map_layers || [];
    }

    public setEnabledMapLayer(layerId: string, enabled: boolean) {
        const enabledMapLayers = this.getEnabledMapLayers();
        if (enabled) {
            if (!enabledMapLayers.includes(layerId)) {
                enabledMapLayers.push(layerId);
            }
        } else {
            const index = enabledMapLayers.indexOf(layerId);
            if (index !== -1) {
                enabledMapLayers.splice(index, 1);
            }
        }
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            settings_readonly: {
                ...(this._accountData?.settings_readonly ?? defaultAccount.settings_readonly),
                enabled_map_layers: enabledMapLayers,
            },
        };
        return this;
    }

    public getSettingsReadonly() {
        return this._accountData?.settings_readonly || null;
    }

    public setSettingsReadonly(settings_readonly: any) {
        this._accountData = {
            ...(this._accountData ?? defaultAccount),
            settings_readonly,
        };
        return this;
    }

    public getDefaultLocale(locale: string): string {
        let ls = this.getLangSettings();
        if (!ls) {
            return locale;
        }
        if (ls.languagesAvailableForUser === null) {
            return locale;
        }
        let fallbackLocale = Object.keys(ls.languagesAvailableForUser)[0] || locale;
        if (ls.languagesAvailableForUser[locale]) {
            return locale;
        }
        return fallbackLocale;
    }

    public setEnabledLocales(locales: string[]): this {
        if (this._accountData) {
            if (this._accountData.settings_readonly) {
                this._accountData.settings_readonly.enabled_locales = locales;
            }
        }
        return this;
    }

    public getEnabledLocales(): string[] {
        return this._accountData?.settings_readonly.enabled_locales || [];
    }

    public getAvailableLocales(): { id: string; name: string }[] {
        let ls = this.getLangSettings();
        if (!ls) {
            return [];
        }
        if (ls.languagesAvailableForUser === null) {
            return [];
        }
        return Object.entries(ls.languagesAvailableForUser).map(([id, name]) => ({ id, name }));
    }

    public getLangSettings(): LangSettingsType | null {
        try {
            return LangSettingsSchema.parse(this._accountData?.lang_settings);
        } catch (e) {
            console.error('Invalid lang settings', e);
            return null;
        }
    }
}
